import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useVehicleLookup, type VehicleLookupResult } from '@/hooks/useVehicleLookup';
import { formatFinnishPlate } from '@/utils/finnishPlate';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';
import { isTemporarilyUnavailable } from '@/lib/describeApiError';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import type { TFunction } from 'i18next';

export interface VehicleFieldsData {
  registrationNumber: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  mileage: string;
}

export const initialVehicleFieldsData: VehicleFieldsData = {
  registrationNumber: '',
  make: '',
  model: '',
  year: '',
  vin: '',
  mileage: '',
};

export type VehicleLookupFieldId =
  | 'make'
  | 'model'
  | 'year'
  | 'vin'
  | 'bodyType'
  | 'color'
  | 'doors'
  | 'seats'
  | 'fuelType'
  | 'engineDisplacementCc'
  | 'enginePowerKw'
  | 'transmission'
  | 'driveType'
  | 'curbWeightKg'
  | 'grossWeightKg'
  | 'lengthMm'
  | 'widthMm'
  | 'heightMm'
  | 'vehicleClass'
  | 'vehicleGroup'
  | 'co2EmissionsGkm'
  | 'euroEmissionClass'
  | 'firstRegistrationDate';

export type OverlayFieldKind = 'text' | 'int' | 'date';

export interface VehicleLookupFieldSpec {
  id: VehicleLookupFieldId;
  lookupKey: keyof VehicleLookupResult;
  overlayKey: string;
  labelKey: string;
  kind: OverlayFieldKind;
  /** Physical unit shown next to the value — not localised (kg, mm, kW, …). */
  unit?: string;
  formKey?: keyof VehicleFieldsData;
}

export const VEHICLE_LOOKUP_FIELDS: VehicleLookupFieldSpec[] = [
  { id: 'make', lookupKey: 'make', overlayKey: 'make', labelKey: 'make', kind: 'text', formKey: 'make' },
  { id: 'model', lookupKey: 'model', overlayKey: 'model', labelKey: 'model', kind: 'text', formKey: 'model' },
  { id: 'year', lookupKey: 'year', overlayKey: 'modelYear', labelKey: 'year', kind: 'int', formKey: 'year' },
  { id: 'vin', lookupKey: 'vin', overlayKey: 'vin', labelKey: 'vin', kind: 'text', formKey: 'vin' },
  { id: 'bodyType', lookupKey: 'bodyType', overlayKey: 'bodyType', labelKey: 'traficom.bodyType', kind: 'text' },
  { id: 'color', lookupKey: 'color', overlayKey: 'color', labelKey: 'color', kind: 'text' },
  { id: 'doors', lookupKey: 'doors', overlayKey: 'doors', labelKey: 'traficom.doors', kind: 'int' },
  { id: 'seats', lookupKey: 'seats', overlayKey: 'seats', labelKey: 'traficom.seats', kind: 'int' },
  { id: 'fuelType', lookupKey: 'fuelType', overlayKey: 'fuelType', labelKey: 'traficom.fuelType', kind: 'text' },
  {
    id: 'engineDisplacementCc',
    lookupKey: 'engineDisplacementCc',
    overlayKey: 'engineDisplacementCc',
    labelKey: 'traficom.engineDisplacement',
    kind: 'int',
    unit: 'cm³',
  },
  {
    id: 'enginePowerKw',
    lookupKey: 'enginePowerKw',
    overlayKey: 'enginePowerKw',
    labelKey: 'traficom.enginePower',
    kind: 'int',
    unit: 'kW',
  },
  {
    id: 'transmission',
    lookupKey: 'transmission',
    overlayKey: 'transmission',
    labelKey: 'transmission',
    kind: 'text',
  },
  { id: 'driveType', lookupKey: 'driveType', overlayKey: 'driveType', labelKey: 'traficom.driveType', kind: 'text' },
  {
    id: 'curbWeightKg',
    lookupKey: 'curbWeightKg',
    overlayKey: 'curbWeightKg',
    labelKey: 'traficom.curbWeight',
    kind: 'int',
    unit: 'kg',
  },
  {
    id: 'grossWeightKg',
    lookupKey: 'grossWeightKg',
    overlayKey: 'grossWeightKg',
    labelKey: 'traficom.grossWeight',
    kind: 'int',
    unit: 'kg',
  },
  { id: 'lengthMm', lookupKey: 'lengthMm', overlayKey: 'lengthMm', labelKey: 'traficom.length', kind: 'int', unit: 'mm' },
  { id: 'widthMm', lookupKey: 'widthMm', overlayKey: 'widthMm', labelKey: 'traficom.width', kind: 'int', unit: 'mm' },
  { id: 'heightMm', lookupKey: 'heightMm', overlayKey: 'heightMm', labelKey: 'traficom.height', kind: 'int', unit: 'mm' },
  {
    id: 'vehicleClass',
    lookupKey: 'vehicleClass',
    overlayKey: 'vehicleClass',
    labelKey: 'traficom.vehicleClass',
    kind: 'text',
  },
  {
    id: 'vehicleGroup',
    lookupKey: 'vehicleGroup',
    overlayKey: 'vehicleGroup',
    labelKey: 'traficom.vehicleGroup',
    kind: 'text',
  },
  {
    id: 'co2EmissionsGkm',
    lookupKey: 'co2EmissionsGkm',
    overlayKey: 'co2EmissionsGkm',
    labelKey: 'traficom.co2',
    kind: 'int',
    unit: 'g/km',
  },
  {
    id: 'euroEmissionClass',
    lookupKey: 'euroEmissionClass',
    overlayKey: 'euroEmissionClass',
    labelKey: 'traficom.euroClass',
    kind: 'text',
  },
  {
    id: 'firstRegistrationDate',
    lookupKey: 'firstRegistrationDate',
    overlayKey: 'firstRegistrationDate',
    labelKey: 'traficom.firstRegistration',
    kind: 'date',
  },
];

export const VEHICLE_FORM_LOOKUP_FIELDS: VehicleLookupFieldSpec[] = VEHICLE_LOOKUP_FIELDS.filter(
  (field) => field.formKey != null,
);

export function normalizeLookupValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') return value.trim();
  return String(value);
}

/** A VIN worth keeping. Null, '' and whitespace are not a VIN (#4122). */
export function nonBlankVin(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function lookupValuesEqual(a: unknown, b: unknown): boolean {
  return normalizeLookupValue(a) === normalizeLookupValue(b);
}

export function overlayHasKey(overrides: Record<string, unknown> | null | undefined, key: string): boolean {
  return !!overrides && Object.prototype.hasOwnProperty.call(overrides, key);
}

/** Display: manual_overrides.<field> ?? traficom_data.<field> (?? column). */
export function displayedOverlayValue(
  overlayKey: string,
  overrides: Record<string, unknown> | null | undefined,
  traficom: Record<string, unknown> | null | undefined,
  fallback?: unknown,
): unknown {
  if (overlayHasKey(overrides, overlayKey)) {
    return overrides![overlayKey];
  }
  if (traficom && Object.prototype.hasOwnProperty.call(traficom, overlayKey) && traficom[overlayKey] != null) {
    return traficom[overlayKey];
  }
  return fallback;
}

export interface VehicleLookupDiffItem {
  id: VehicleLookupFieldId;
  overlayKey: string;
  labelKey: string;
  current: string;
  next: string;
  defaultSelected: boolean;
  formKey?: keyof VehicleFieldsData;
}

export function buildVehicleLookupDiff(args: {
  lookup: VehicleLookupResult;
  currentByOverlayKey: Record<string, unknown>;
  overrideKeys?: Iterable<string>;
  fields?: VehicleLookupFieldSpec[];
}): VehicleLookupDiffItem[] {
  const overrideSet = new Set(args.overrideKeys ?? []);
  const fields = args.fields ?? VEHICLE_LOOKUP_FIELDS;
  const items: VehicleLookupDiffItem[] = [];

  for (const spec of fields) {
    const nextRaw = args.lookup[spec.lookupKey];
    if (nextRaw == null || normalizeLookupValue(nextRaw) === '') continue;
    const currentRaw = args.currentByOverlayKey[spec.overlayKey];
    const current = normalizeLookupValue(currentRaw);
    const next = normalizeLookupValue(nextRaw);
    if (current === next) continue;
    const item: VehicleLookupDiffItem = {
      id: spec.id,
      overlayKey: spec.overlayKey,
      labelKey: spec.labelKey,
      current,
      next,
      defaultSelected: !overrideSet.has(spec.overlayKey),
    };
    if (spec.formKey !== undefined) {
      item.formKey = spec.formKey;
    }
    items.push(item);
  }

  return items;
}

export interface AppliedLookupResult {
  traficomData: Record<string, unknown>;
  manualOverrides: Record<string, unknown> | null;
  columns: {
    make?: string;
    model?: string;
    year?: number;
    vin?: string;
  };
}

/**
 * Apply a registry lookup: write the full snapshot to traficom_data, drop
 * overrides for selected fields, and freeze the current display of unselected
 * fields as overrides so the card does not jump.
 */
export function applyLookupToOverlay(args: {
  lookup: VehicleLookupResult;
  selectedIds: Iterable<VehicleLookupFieldId>;
  currentOverrides: Record<string, unknown> | null | undefined;
  currentDisplay: Record<string, unknown>;
  existingTraficom: Record<string, unknown> | null | undefined;
  registrationNumber: string;
}): AppliedLookupResult {
  const selected = new Set(args.selectedIds);
  // A registry response with no VIN must not wipe a VIN we already have (#4122).
  const keptVin =
    nonBlankVin(args.lookup.vin)
    ?? nonBlankVin(args.existingTraficom?.['vin'])
    ?? nonBlankVin(args.currentDisplay['vin']);
  const traficomData: Record<string, unknown> = {
    ...(args.existingTraficom ?? {}),
    ...args.lookup.rawData,
    make: args.lookup.make,
    model: args.lookup.model,
    modelYear: args.lookup.year,
    vin: keptVin ?? args.lookup.vin,
    bodyType: args.lookup.bodyType,
    color: args.lookup.color,
    doors: args.lookup.doors,
    seats: args.lookup.seats,
    fuelType: args.lookup.fuelType,
    engineDisplacementCc: args.lookup.engineDisplacementCc,
    enginePowerKw: args.lookup.enginePowerKw,
    transmission: args.lookup.transmission,
    driveType: args.lookup.driveType,
    curbWeightKg: args.lookup.curbWeightKg,
    grossWeightKg: args.lookup.grossWeightKg,
    lengthMm: args.lookup.lengthMm,
    widthMm: args.lookup.widthMm,
    heightMm: args.lookup.heightMm,
    vehicleClass: args.lookup.vehicleClass,
    vehicleGroup: args.lookup.vehicleGroup,
    co2EmissionsGkm: args.lookup.co2EmissionsGkm,
    euroEmissionClass: args.lookup.euroEmissionClass,
    firstRegistrationDate: args.lookup.firstRegistrationDate,
    registrationNumber: args.registrationNumber,
    fetchedAt: args.lookup.fetchedAt,
  };

  const newOverrides: Record<string, unknown> = { ...(args.currentOverrides ?? {}) };
  const columns: AppliedLookupResult['columns'] = {};

  for (const spec of VEHICLE_LOOKUP_FIELDS) {
    const nextVal = args.lookup[spec.lookupKey];
    if (nextVal == null || normalizeLookupValue(nextVal) === '') continue;
    const currentVal = args.currentDisplay[spec.overlayKey];
    if (lookupValuesEqual(currentVal, nextVal)) continue;

    if (selected.has(spec.id)) {
      delete newOverrides[spec.overlayKey];
      if (spec.id === 'make') columns.make = String(nextVal);
      if (spec.id === 'model') columns.model = String(nextVal);
      if (spec.id === 'year') columns.year = Number(nextVal);
      if (spec.id === 'vin') {
        const vin = nonBlankVin(nextVal);
        if (vin !== null) columns.vin = vin;
      }
    } else if (currentVal === undefined) {
      newOverrides[spec.overlayKey] = null;
    } else {
      newOverrides[spec.overlayKey] = currentVal;
    }
  }

  return {
    traficomData,
    manualOverrides: Object.keys(newOverrides).length === 0 ? null : newOverrides,
    columns,
  };
}

export function validateOverlayInput(
  spec: VehicleLookupFieldSpec,
  raw: string,
  t: TFunction,
): string | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;

  if (spec.kind === 'date') {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return t('form.dateInvalid');
    return undefined;
  }

  if (spec.kind !== 'int') return undefined;

  const num = Number(trimmed);
  if (!Number.isFinite(num) || !Number.isInteger(num) || num < 0) {
    return t('form.numberInvalid');
  }

  switch (spec.id) {
    case 'year':
      if (num < 1900 || num > 2100) return t('form.yearInvalid');
      return undefined;
    case 'doors':
      if (num > 20) return t('form.doorsInvalid');
      return undefined;
    case 'seats':
      if (num > 99) return t('form.seatsInvalid');
      return undefined;
    case 'engineDisplacementCc':
      if (num > 20_000) return t('form.displacementInvalid');
      return undefined;
    case 'enginePowerKw':
      if (num > 2000) return t('form.powerInvalid');
      return undefined;
    case 'curbWeightKg':
    case 'grossWeightKg':
      if (num > 100_000) return t('form.weightInvalid');
      return undefined;
    case 'lengthMm':
    case 'widthMm':
    case 'heightMm':
      if (num > 100_000) return t('form.dimensionInvalid');
      return undefined;
    case 'co2EmissionsGkm':
      if (num > 2000) return t('form.co2Invalid');
      return undefined;
    default:
      return undefined;
  }
}

export function parseOverlayInput(spec: VehicleLookupFieldSpec, raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (spec.kind === 'int') return Number(trimmed);
  return trimmed;
}

export function validateVehicleFields(
  data: VehicleFieldsData,
  t: TFunction,
): Partial<Record<keyof VehicleFieldsData, string>> {
  const errors: Partial<Record<keyof VehicleFieldsData, string>> = {};

  if (!data.registrationNumber.trim()) {
    errors.registrationNumber = t('form.registrationRequired');
  } else if (data.registrationNumber.trim().length > 20) {
    errors.registrationNumber = t('form.registrationRequired');
  }
  if (data.year) {
    const yearNum = Number(data.year);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > 2100) {
      errors.year = t('form.yearInvalid');
    }
  }
  if (data.vin && data.vin.trim().length > 17) {
    errors.vin = t('form.vinMaxLength');
  }
  if (data.mileage) {
    const mileageNum = Number(data.mileage);
    if (isNaN(mileageNum) || mileageNum < 0) {
      errors.mileage = t('form.mileageInvalid');
    }
  }

  return errors;
}

export interface VehicleLookupDiffDialogProps {
  open: boolean;
  items: VehicleLookupDiffItem[];
  onClose: () => void;
  onApply: (selectedIds: VehicleLookupFieldId[]) => void;
}

export function VehicleLookupDiffDialog({
  open,
  items,
  onClose,
  onApply,
}: VehicleLookupDiffDialogProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    const next: Record<string, boolean> = {};
    for (const item of items) {
      next[item.id] = item.defaultSelected;
    }
    setSelected(next);
  }, [open, items]);

  const handleApply = () => {
    onApply(items.filter((item) => selected[item.id]).map((item) => item.id));
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('lookup.diffTitle')}
    >
      <DialogHeader onClose={onClose} title={t('lookup.diffTitle')} />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('lookup.diffDescription')}
        </Typography>
        <Stack spacing={1}>
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 0.5,
              }}
            >
              <FormControlLabel
                sx={{ alignItems: 'flex-start', mr: 0, flex: 1, m: 0 }}
                control={
                  <Checkbox
                    checked={selected[item.id] === true}
                    onChange={(event) => {
                      setSelected((prev) => ({ ...prev, [item.id]: event.target.checked }));
                    }}
                    sx={{ pt: 0 }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {t(item.labelKey)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {item.current === '' ? t('lookup.emptyValue') : item.current}
                      {' → '}
                      {item.next}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button variant="contained" onClick={handleApply}>
          {t('lookup.applySelected')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

interface VehicleFormFieldsProps {
  data: VehicleFieldsData;
  errors: Partial<Record<keyof VehicleFieldsData, string>>;
  onChange: (field: keyof VehicleFieldsData, value: string) => void;
  showLookup?: boolean;
}

export function VehicleFormFields({
  data,
  errors,
  onChange,
  showLookup = true,
}: VehicleFormFieldsProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const lookupMutation = useVehicleLookup();
  const [registrySourced, setRegistrySourced] = useState<Set<string>>(() => new Set());
  const [diffItems, setDiffItems] = useState<VehicleLookupDiffItem[] | null>(null);
  const [pendingLookup, setPendingLookup] = useState<VehicleLookupResult | null>(null);

  const handleFieldChange = (field: keyof VehicleFieldsData, value: string) => {
    setRegistrySourced((prev) => {
      const overlayKey = field === 'year' ? 'modelYear' : field;
      if (!prev.has(overlayKey)) return prev;
      const next = new Set(prev);
      next.delete(overlayKey);
      return next;
    });
    onChange(field, value);
  };

  const handleLookup = async () => {
    const reg = data.registrationNumber.trim();
    const plate = formatFinnishPlate(reg);
    if (plate === null) return;

    try {
      const result = await lookupMutation.mutateAsync({ registrationNumber: plate });
      const currentByOverlayKey: Record<string, unknown> = {
        make: data.make,
        model: data.model,
        modelYear: data.year,
        vin: data.vin,
      };
      const overrideKeys: string[] = [];
      for (const spec of VEHICLE_FORM_LOOKUP_FIELDS) {
        const current = currentByOverlayKey[spec.overlayKey];
        if (normalizeLookupValue(current) !== '' && !registrySourced.has(spec.overlayKey)) {
          overrideKeys.push(spec.overlayKey);
        }
      }
      const items = buildVehicleLookupDiff({
        lookup: result,
        currentByOverlayKey,
        overrideKeys,
        fields: VEHICLE_FORM_LOOKUP_FIELDS,
      });
      if (items.length === 0) {
        notify.info(t('lookup.noChanges'), t('lookup.noChangesDesc'));
        return;
      }
      setPendingLookup(result);
      setDiffItems(items);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          notify.warning(t('lookup.notFound'), t('lookup.notFoundDesc'));
          return;
        }
        if (isTemporarilyUnavailable(error)) {
          notify.warning(tc('error'), tc('errors.temporarilyUnavailable'));
          return;
        }
        if (error.status === 503) {
          notify.warning(t('lookup.notConfigured'), t('lookup.notConfiguredDesc'));
          return;
        }
      }
      notify.error(tc('error'), t('lookup.failedDesc'));
    }
  };

  const handleCloseDiff = () => {
    setDiffItems(null);
    setPendingLookup(null);
  };

  const handleApplyDiff = (selectedIds: VehicleLookupFieldId[]) => {
    const lookup = pendingLookup;
    const items = diffItems ?? [];
    handleCloseDiff();
    if (!lookup) return;

    const selected = new Set(selectedIds);
    setRegistrySourced((prev) => {
      const next = new Set(prev);
      for (const item of items) {
        if (!selected.has(item.id)) continue;
        next.add(item.overlayKey);
      }
      return next;
    });
    for (const item of items) {
      if (!selected.has(item.id) || item.formKey == null) continue;
      onChange(item.formKey, item.next);
    }
    notify.success(t('lookup.found'), t('lookup.appliedDesc'));
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label={t('registrationNumber')}
          value={data.registrationNumber}
          onChange={(e) => { handleFieldChange('registrationNumber', e.target.value.toUpperCase()); }}
          error={!!errors.registrationNumber}
          helperText={errors.registrationNumber ?? t('registrationNumberHint')}
          fullWidth
          required
        />
        {showLookup && (
          <Tooltip title={t('lookup.tooltip')}>
            <span>
              <IconButton
                onClick={() => void handleLookup()}
                disabled={formatFinnishPlate(data.registrationNumber) === null || lookupMutation.isPending}
                color="primary"
                sx={{ mt: 1 }}
                aria-label={t('lookup.tooltip')}
              >
                {lookupMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      <TextField
        label={t('make')}
        value={data.make}
        onChange={(e) => { handleFieldChange('make', e.target.value); }}
        fullWidth
      />

      <TextField
        label={t('model')}
        value={data.model}
        onChange={(e) => { handleFieldChange('model', e.target.value); }}
        fullWidth
      />

      <TextField
        label={t('year')}
        value={data.year}
        onChange={(e) => { handleFieldChange('year', e.target.value); }}
        error={!!errors.year}
        helperText={errors.year}
        fullWidth
        type="number"
      />

      <TextField
        label={t('vin')}
        value={data.vin}
        onChange={(e) => { handleFieldChange('vin', e.target.value); }}
        error={!!errors.vin}
        helperText={errors.vin ?? t('vinHint')}
        fullWidth
      />

      <TextField
        label={t('mileage')}
        value={data.mileage}
        onChange={(e) => { handleFieldChange('mileage', e.target.value); }}
        error={!!errors.mileage}
        helperText={errors.mileage}
        fullWidth
        type="number"
      />

      <VehicleLookupDiffDialog
        open={diffItems != null}
        items={diffItems ?? []}
        onClose={handleCloseDiff}
        onApply={handleApplyDiff}
      />
    </>
  );
}
